import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip as MuiTooltip,
} from '@mui/material';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { useAuth } from '../context/AuthContext';
import GexMapView from './GexMapView';

// ── Types (mirror of eod_opt_spy_daily_YYYY-MM-DD.json) ──────────────────────

interface GexRead {
  underlying: string;
  spot: number;
  computed_at: string;
  expiries: string[];
  net_gex: number;
  regime: 'positive' | 'negative' | 'mixed';
  flip_point: number;
  call_wall: number;
  put_wall: number;
  magnet: number;
  // v0.9.1: annualized same-day ATM implied vol; absent/null in older files.
  // Since v0.10.0 it also gates negative-regime admission (calm enough = trade half size).
  // The cap is config-driven (negative_regime_iv_max): 12% → 14% → 16% → 16.5% (v0.15.1, 8/21).
  // v0.13.0 adds a high-side veto (atm_iv_veto, default 25%): above it, no trade in ANY regime.
  atm_iv?: number | null;
  // v0.11.2: overnight gap data point (absent in older files); logged, not yet a gate.
  prev_close?: number | null;
  gap_pct?: number | null;
  gap_vs_sigma?: number | null;
  // v0.16.1: today's economic-calendar event, also top-level as event_today
  event_today?: string | null;
  // v0.17.2: overhead-profile metrics — DATA ONLY, the selector never reads
  // them (also inside each trade's gex_at_entry). Absent in pre-v0.17.2 files.
  stair_up?: number | null;
  neg_shelf?: number | null;
  shadow_call_fade_veto?: boolean | null;
}

// v0.9.0: per-account roll-up carried on the LEADER's file
interface AccountSummaryRow {
  account: string;
  is_leader: boolean;
  trades_today: number;
  realized_pnl_today: number;
  realized_pnl_all_time: number;
  closed_all_time: number;
  wins_all_time: number;
}

interface OptSpyTrade {
  direction: 'put_credit' | 'call_credit';
  underlying: string;
  short_strike: number;
  long_strike: number;
  width: number;
  expiry: string;
  contracts: number;
  // v0.27.0: contracts < contracts_requested = partial entry ("1 of 30
  // filled"), never a 1-contract trade; fill_reason: filled |
  // partial_fill_max_chase | partial_fill_cancelled. Absent = full fill.
  contracts_requested?: number | null;
  fill_reason?: string | null;
  opened_at: string;
  entry_credit_per_share: number;
  entry_credit_total: number;
  max_loss_total: number;
  status: string;
  closed_at: string | null;
  exit_kind: string | null;
  exit_reason: string | null;
  close_debit_per_share: number | null;
  realized_pnl: number;
  pct_of_credit_captured: number | null;
  // v0.17.2: entry-time gamma read carries the overhead-profile shadow signal
  // v0.28.0/v0.30.0: wall_strike_fallback non-null = the short leg IS the wall
  // strike (fallback); wall_strike_preference.fired = sold the wall (preference)
  gex_at_entry?: {
    stair_up?: number | null;
    shadow_call_fade_veto?: boolean | null;
    wall_strike_fallback?: { wall?: number } | null;
    wall_strike_preference?: { wall?: number; would_fire?: boolean; fired?: boolean } | null;
  } | null;
}

// v0.27.0: partial-entry helpers — shared by the one-liner, the today panel
// and the trade log table
const isPartialFill = (t: OptSpyTrade): boolean =>
  t.contracts_requested != null && t.contracts != null && t.contracts < t.contracts_requested;
const contractsLabel = (t: OptSpyTrade): string =>
  isPartialFill(t) ? `${t.contracts} of ${t.contracts_requested} filled` : String(t.contracts);
// v0.28.0/v0.30.0: one short suffix when the short leg is the wall strike itself
const wallStrikeNote = (t: OptSpyTrade): string =>
  t.gex_at_entry?.wall_strike_preference?.fired
    ? ' · wall strike (preference)'
    : t.gex_at_entry?.wall_strike_fallback
    ? ' · wall strike (fallback)'
    : '';

interface NoTradeReason {
  ts_et: string;
  action: string;
  reason: string;
  // v0.19.0: EVENT_BLACKOUT rows carry the event name (one row per scan)
  event?: string | null;
}

interface OptSpyDay {
  date_et: string;
  generated_at_et: string;
  strategy: string;
  version?: string;
  account_id: string;
  is_leader?: boolean;
  accounts_summary?: AccountSummaryRow[];
  // v0.16.1: today's economic-calendar event (CPI/NFP/PPI/PCE/FOMC) or null
  event_today?: string | null;
  config: {
    underlying: string;
    spread_width: number;
    profit_close_pct: number;
    stop_loss_mult: number;
    max_loss_budget_pct: number;
    max_trades_per_day: number;
    forced_close_et: string;
  };
  account: {
    equity: number;
    last_equity: number;
    cash: number;
    options_buying_power: number;
    status: string;
  };
  gex: GexRead | null;
  trades_today: OptSpyTrade[];
  no_trade_reasons: NoTradeReason[];
  summary: {
    trades_today: number;
    premium_collected_today: number;
    realized_pnl_today: number;
    realized_pnl_all_time: number;
    closed_all_time: number;
    wins_all_time: number;
    losses_all_time: number;
    win_rate: number | null; // null until the account has a closed trade
  };
}

interface OptSpyDailyViewProps {
  instanceName: string;
}

// ── Formatting helpers ────────────────────────────────────────────────────────

// Accounts trading real money (9/7: Jacob_A joined as a second LIVE account —
// a follower, but same LIVE treatment as the leader, not paper). The EOD files
// carry no live/paper flag, so this is keyed by account id / file suffix.
// BoazL added 9/24 (v0.36.1): third LIVE follower — unfunded and
// roster-disabled at add time; its state/EOD files appear on first enabled day
const LIVE_ACCOUNT_IDS = new Set(['Ilan-Live', 'Jacob_A', 'BoazL']);
const LIVE_ACCOUNT_SUFFIXES = new Set(['jacob_a', 'boazl']);

const fmtCurrency = (v: number): string => {
  const sign = v < 0 ? '-' : '';
  return `${sign}$${Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const fmtSignedCurrency = (v: number): string => (v >= 0 ? `+${fmtCurrency(v)}` : fmtCurrency(v));

const fmtStrike = (v: number): string => (Number.isInteger(v) ? String(v) : v.toFixed(2).replace(/0+$/, '').replace(/\.$/, ''));

const strikesLabel = (t: OptSpyTrade): string =>
  `${fmtStrike(t.short_strike)}/${fmtStrike(t.long_strike)}${t.direction === 'call_credit' ? 'c' : 'p'}`;

const fmtTimeEt = (iso: string | null): string => {
  if (!iso) return '—';
  // Timestamps are already ET-offset ISO strings; show the wall-clock portion.
  const m = iso.match(/T(\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : iso;
};

const holdTime = (opened: string, closed: string | null): string => {
  if (!closed) return '—';
  const mins = Math.round((new Date(closed).getTime() - new Date(opened).getTime()) / 60000);
  if (mins < 0 || isNaN(mins)) return '—';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const prettyNoTradeReason = (r: NoTradeReason): string => {
  if (r.reason.startsWith('event_veto:')) {
    return `Event veto — ${r.reason.slice('event_veto:'.length)}`;
  }
  // v0.19.0: entries deliberately blocked through a morning-session event
  // (scans and exits keep running) — not a malfunction
  if (r.reason.startsWith('event_blackout_until_')) {
    const hhmm = r.reason.slice('event_blackout_until_'.length);
    return `Deliberately standing aside through ${r.event || 'the scheduled event'} — scanning continues, entries resume ${hhmm} ET`;
  }
  if (r.reason.startsWith('negative_regime_iv_too_high')) {
    return 'Stood aside — dealers short gamma AND options pricing real movement (low-IV exception did not apply, and no call wall was there to fade)';
  }
  if (r.reason.startsWith('atm_iv_veto')) {
    return 'Stood aside — options pricing a panic-sized move (IV above the veto cap); no structure trusted in any regime';
  }
  if (r.reason.startsWith('credit_too_thin')) {
    // v0.27.0 appends the priced strikes (…_771/773); v0.28.0 may append an
    // |at_wall_… suffix = the wall strike was also tried as a fallback and
    // refused (sigma too close / inside min OTM / too thin / no quote)
    const [main, atWall] = r.reason.split('|');
    const strikes = main.match(/_(\d+(?:\.\d+)?\/\d+(?:\.\d+)?)$/);
    let text = `Stood aside — the spread${strikes ? ` (${strikes[1]})` : ''} didn't pay enough`;
    if (atWall) {
      const wallStrike = atWall.match(/^at_wall_(\d+(?:\.\d+)?)/)?.[1];
      const at = wallStrike ? `the wall strike (${wallStrike})` : 'the wall strike';
      text += atWall.includes('sigma')
        ? `; ${at} was also tried but sits too close to spot`
        : atWall.includes('inside_min_otm')
        ? `; ${at} was also tried but is inside the minimum OTM distance`
        : atWall.includes('credit_too_thin')
        ? `; ${at} was also tried — it didn't pay enough either`
        : atWall.includes('no_quote')
        ? `; ${at} was also tried but had no usable quote`
        : `; ${at} was also tried as a fallback and refused`;
    }
    return text;
  }
  // v0.25.1: the protective long leg had no bid — a no-premium morning
  // (older logs say no_long_leg_at_width)
  if (r.reason.startsWith('long_leg_no_quote') || r.reason === 'no_long_leg_at_width') {
    return 'Stood aside — the protective leg had no bid at all (the market priced that side at zero, so there was no spread to sell)';
  }
  if (r.reason.startsWith('long_leg_quote_too_wide')) {
    return 'Stood aside — the protective leg was quoted, but too wide to trust';
  }
  // v0.68.0 down-day call gate
  if (r.reason.includes('down_day_call_block')) {
    const m = r.reason.match(/down_day_call_block_([+-]?[\d.]+)/);
    return `Stood aside — SPY ${m ? `${m[1].startsWith('-') ? '' : '−'}${m[1].replace('-', '−')}%` : 'down ≥ 1%'} on the day; no call spreads after a selloff`;
  }
  // v0.54.0 LIVE wall-stability entry gate: the mode of the last 5 wall
  // reads would not clear the cushion floor (the raw read did)
  if (r.reason.startsWith('wall_unstable_mode5')) {
    const m = r.reason.match(/wall_unstable_mode5_(put|call)_([\d.]+)_vs_raw_([\d.]+)/);
    return m
      ? `Stood aside — the ${m[1]} wall is unstable: the stabilized read (mode of last 5 = ${m[2]}) wouldn't clear the cushion floor the raw read (${m[3]}) shows`
      : 'Stood aside — the wall is unstable: the stabilized read (mode of last 5) wouldn\'t clear the cushion floor';
  }
  // v0.36.0: MAP_CONVICTION_VETO days — the regime label itself wasn't
  // trusted (flips=0 = net too weak; flips>0 = sign flipped through the open)
  if (r.reason.startsWith('map_undecided')) {
    const m = r.reason.match(/flips=(\d+)_net=([+-][\d.]+M)/);
    if (m && m[1] === '0') return `Stood aside — the dealer map was too weak to trust (net ${m[2]}); no regime call`;
    if (m) return `Stood aside — the dealer map flipped sign ${m[1]}× through the open (net ${m[2]}); no trustworthy regime`;
    return 'Stood aside — the dealer map was too weak or unstable to trust';
  }
  // v0.17.1: cushion rejects show the applied bar (0.1% put / 0.3% call) and
  // may be `call:` prefixed — a call-side reject followed by a put-side entry
  // on the same scan is the selector rerouting to the safer side, not an error
  const isCallCushion = r.reason.startsWith('call:');
  const cushionReason = isCallCushion ? r.reason.slice('call:'.length) : r.reason;
  if (cushionReason.startsWith('exit_cushion_too_thin')) {
    const m = cushionReason.match(/([\d.]+)%<([\d.]+)%/);
    const nums = m ? ` (${m[1]}% cushion vs the ${m[2]}%${isCallCushion ? ' call-side' : ''} bar)` : '';
    return `Stood aside — the exit line was too close to the price${nums} (noise would stop us out immediately)${isCallCushion ? '; the system then rechecks the put side' : ''}`;
  }
  if (r.reason.startsWith('unfilled_after') || r.reason === 'chase_floor_reached') {
    return "No fill — the market wouldn't fill us at a price worth taking (credit floor held)";
  }
  const known: { [key: string]: string } = {
    negative_regime_stand_aside: 'Stood aside — negative gamma regime',
    mixed_regime_stand_aside: 'Stood aside — mixed gamma regime',
  };
  return known[r.reason] || r.reason.replace(/_/g, ' ');
};

// Repeated per-scan no-trade rows (EVENT_BLACKOUT one per scan, IV gates one
// per retry with drifting numbers) collapse to one line per distinct reason;
// the latest row of each group supplies the displayed numbers/timestamp.
const dedupeNoTradeReasons = (rows: NoTradeReason[]): { row: NoTradeReason; count: number }[] => {
  const groups = new Map<string, { row: NoTradeReason; count: number }>();
  rows.forEach((r) => {
    const key = `${r.action}|${r.reason.replace(/[\d.:]+/g, '#')}`;
    const g = groups.get(key);
    if (g) {
      g.count += 1;
      g.row = r;
    } else {
      groups.set(key, { row: r, count: 1 });
    }
  });
  return Array.from(groups.values());
};

// Wins-by-design are green, controlled losses red; forced/event closes go by P&L sign.
const EXIT_REASON_LABELS: { [key: string]: string } = {
  profit_close: 'PROFIT CLOSE',
  expired_worthless: 'EXPIRED WORTHLESS',
  max_loss_close: 'MAX LOSS STOP',
  short_strike_breach: 'STRIKE BREACH',
  flip_breach: 'FLIP BREACH',
  wall_breach: 'WALL BREACH',
  forced_eod_close: 'FORCED EOD CLOSE',
  event_close: 'EVENT CLOSE',
  // v0.31.0: operator asked for an immediate exit via the close-now flag file
  operator_close: 'OPERATOR CLOSE',
  // v0.54.0 LIVE exits (deliberate closes; color follows the P&L sign)
  giveback_close: 'GIVEBACK',
  ai_exit: 'AI EXIT',
  // v0.56.0 LIVE: fires only after 35% captured — locks a win by design
  profit_floor_close: 'PROFIT FLOOR',
  // v0.69.0 LIVE: the AI chose the exit after the floor fired
  floor_ai_exit: 'FLOOR AI',
};

const exitReasonChip = (reason: string | null, pnl: number) => {
  const label = reason ? (EXIT_REASON_LABELS[reason] || reason.replace(/_/g, ' ').toUpperCase()) : '—';
  let color: 'success' | 'error' | 'warning' | 'default' = 'default';
  if (reason === 'profit_close' || reason === 'expired_worthless' || reason === 'profit_floor_close') color = 'success';
  else if (reason === 'max_loss_close' || reason === 'short_strike_breach' || reason === 'flip_breach' || reason === 'wall_breach') color = 'error';
  else if (reason === 'forced_eod_close' || reason === 'event_close' || reason === 'operator_close'
    || reason === 'giveback_close' || reason === 'ai_exit' || reason === 'floor_ai_exit') color = pnl >= 0 ? 'success' : 'warning';
  return <Chip label={label} size="small" color={color} sx={{ fontWeight: 'bold', fontSize: '0.65rem' }} />;
};

// v0.9.1: 0DTE ATM implied-vol badge — calm ≤ ~15%, elevated 15–25%, high > 25%
const ivBadge = (atmIv: number | null | undefined) => {
  if (atmIv == null) return null;
  const pct = atmIv * 100;
  const color: 'success' | 'warning' | 'error' = pct <= 15 ? 'success' : pct <= 25 ? 'warning' : 'error';
  return (
    <Chip
      label={`0DTE IV ${pct.toFixed(1)}%`}
      color={color}
      size="small"
      variant="outlined"
      sx={{ fontWeight: 'bold', fontSize: '0.65rem' }}
    />
  );
};

// v0.11.2: overnight-gap badge next to the IV badge, e.g. "gap −0.9% (−0.8σ)"
const gapBadge = (gapPct: number | null | undefined, gapVsSigma: number | null | undefined) => {
  if (gapPct == null) return null;
  const pct = gapPct * 100;
  const sigma = gapVsSigma != null ? ` (${gapVsSigma >= 0 ? '+' : ''}${gapVsSigma.toFixed(1)}σ)` : '';
  return (
    <Chip
      label={`gap ${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%${sigma}`}
      color={gapVsSigma != null && Math.abs(gapVsSigma) >= 1 ? 'warning' : 'default'}
      size="small"
      variant="outlined"
      sx={{ fontWeight: 'bold', fontSize: '0.65rem' }}
    />
  );
};

// v0.16.1: economic-calendar badge, e.g. "CPI day". Data only — on FOMC days
// everything is force-closed by 13:30 ET, so that badge warns.
const shortEventName = (e: string): string => {
  const s = e.toLowerCase();
  if (s.includes('fomc')) return 'FOMC';
  if (s.includes('consumer price')) return 'CPI';
  if (s.includes('nonfarm') || s.includes('non-farm')) return 'NFP';
  if (s.includes('producer price')) return 'PPI';
  if (s.includes('pce') || s.includes('personal consumption')) return 'PCE';
  return e;
};

const eventBadge = (event?: string | null) => {
  if (!event) return null;
  const short = shortEventName(event);
  const isFomc = short === 'FOMC';
  return (
    <MuiTooltip title={isFomc ? `${event} — everything is force-closed by 13:30 ET` : event} arrow>
      <Chip
        label={`${short} day`}
        size="small"
        variant="outlined"
        color={isFomc ? 'warning' : 'default'}
        sx={{ fontWeight: 'bold', fontSize: '0.65rem' }}
      />
    </MuiTooltip>
  );
};

// One-line trade summary for follower cards, e.g. "SPY 738/733p × 24 · credit $252 · PROFIT CLOSE +$156"
const tradeOneLiner = (t: OptSpyTrade): string => {
  const exit = t.exit_reason ? (EXIT_REASON_LABELS[t.exit_reason] || t.exit_reason.replace(/_/g, ' ').toUpperCase()) : 'OPEN';
  return `${t.underlying} ${strikesLabel(t)} × ${contractsLabel(t)}${wallStrikeNote(t)} · credit ${fmtCurrency(t.entry_credit_total)} · ${exit} ${fmtSignedCurrency(t.realized_pnl)}`;
};

// ── Component ─────────────────────────────────────────────────────────────────

const OptSpyDailyView: React.FC<OptSpyDailyViewProps> = ({ instanceName }) => {
  const { token } = useAuth();
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [days, setDays] = useState<OptSpyDay[]>([]);
  // v0.9.0 multi-account: follower day-lists keyed by account suffix (e.g. "opt_day_1")
  const [followers, setFollowers] = useState<{ [suffix: string]: OptSpyDay[] }>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const authHeaders = useCallback((): HeadersInit => ({ Authorization: `Bearer ${token}` }), [token]);

  // v0.69.0 LIVE floor AI scoreboard: leader FLOOR_AI_CLOSE rows (since 10/30)
  const [floorAi, setFloorAi] = useState<{ trades: { [k: string]: any }[]; totals: { n: number; n_fallback: number; sum_vs_fixed_floor_per_ct: number | null; sum_pnl_per_ct: number; worst_vs_fixed_floor_per_ct: number | null } } | null>(null);
  useEffect(() => {
    fetch(`/api/instances/${instanceName}/opt-spy-floor-ai?days=120`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setFloorAi(d))
      .catch(() => { /* panel just stays hidden */ });
  }, [instanceName, authHeaders]);

  // Load available dates; default range = everything (feed is daily and self-contained)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/instances/${instanceName}/opt-spy-daily/dates`, { headers: authHeaders() });
        if (!res.ok) throw new Error(`Failed to load dates (${res.status})`);
        const data = await res.json();
        if (cancelled) return;
        const dates: string[] = data.dates || [];
        setAvailableDates(dates);
        if (dates.length > 0) {
          setStartDate(dates[dates.length - 1]); // oldest
          setEndDate(dates[0]); // newest
        } else {
          setLoading(false);
        }
      } catch (e: any) {
        if (!cancelled) {
          setError(e.message || 'Failed to load OPT-SPY-DAILY dates');
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [instanceName, authHeaders]);

  // Fetch the range in a single call
  useEffect(() => {
    if (!startDate || !endDate) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(
          `/api/instances/${instanceName}/opt-spy-daily?start_date=${startDate}&end_date=${endDate}`,
          { headers: authHeaders() }
        );
        if (!res.ok) throw new Error(`Failed to load feed (${res.status})`);
        const data = await res.json();
        if (cancelled) return;
        const sorted: OptSpyDay[] = (data.days || []).sort(
          (a: OptSpyDay, b: OptSpyDay) => a.date_et.localeCompare(b.date_et)
        );
        setDays(sorted);
        const fol: { [suffix: string]: OptSpyDay[] } = {};
        Object.entries(data.followers || {}).forEach(([suffix, list]) => {
          fol[suffix] = (list as OptSpyDay[]).sort((a, b) => a.date_et.localeCompare(b.date_et));
        });
        setFollowers(fol);
      } catch (e: any) {
        if (!cancelled) setError(e.message || 'Failed to load OPT-SPY-DAILY data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [instanceName, startDate, endDate, authHeaders]);

  // ── Aggregations ──
  const agg = useMemo(() => {
    if (days.length === 0) return null;
    const latest = days[days.length - 1];

    const periodRealized = days.reduce((s, d) => s + (d.summary?.realized_pnl_today || 0), 0);
    const periodPremium = days.reduce((s, d) => s + (d.summary?.premium_collected_today || 0), 0);
    const periodTrades = days.reduce((s, d) => s + (d.summary?.trades_today || 0), 0);

    let cumulative = 0;
    const chartData = days.map((d) => {
      const pnl = d.summary?.realized_pnl_today || 0;
      cumulative += pnl;
      return { date: d.date_et.slice(5), pnl, cumulative, noTrade: (d.summary?.trades_today || 0) === 0 };
    });

    // Win streak: newest → oldest; zero-trade days neither extend nor break it.
    let winStreak = 0;
    for (let i = days.length - 1; i >= 0; i--) {
      const s = days[i].summary;
      if (!s || s.trades_today === 0) continue;
      if (s.realized_pnl_today > 0) winStreak += 1;
      else break;
    }

    const allTrades = days.flatMap((d) =>
      (d.trades_today || []).map((t) => ({ ...t, date_et: d.date_et }))
    );

    const exitBreakdown = Object.entries(
      allTrades.reduce((acc: { [key: string]: { count: number; pnl: number } }, t) => {
        const key = t.exit_reason || 'unknown';
        if (!acc[key]) acc[key] = { count: 0, pnl: 0 };
        acc[key].count += 1;
        acc[key].pnl += t.realized_pnl;
        return acc;
      }, {})
    ).sort((a, b) => b[1].count - a[1].count);

    return { latest, periodRealized, periodPremium, periodTrades, chartData, winStreak, allTrades, exitBreakdown };
  }, [days]);

  // v0.9.0 follower cards: latest day per follower + divergence vs the leader's same day.
  // Divergence (one traded and the other didn't, or exit reasons differ) is signal, not noise.
  const followerCards = useMemo(() => {
    return Object.entries(followers).map(([suffix, fdays]) => {
      const latestDay = fdays[fdays.length - 1];
      const leaderSameDay = days.find((d) => d.date_et === latestDay.date_et) || null;
      const fTrade = latestDay.trades_today.length > 0 ? latestDay.trades_today[0] : null;
      const lTrade = leaderSameDay && leaderSameDay.trades_today.length > 0 ? leaderSameDay.trades_today[0] : null;
      let divergence: string | null = null;
      if (leaderSameDay) {
        if (fTrade && !lTrade) divergence = 'Traded while the leader stood aside';
        else if (!fTrade && lTrade) divergence = 'Stood aside while the leader traded';
        else if (fTrade && lTrade && fTrade.exit_reason !== lTrade.exit_reason) {
          divergence = `Exit diverged from leader: ${(fTrade.exit_reason || 'open').replace(/_/g, ' ')} vs ${(lTrade.exit_reason || 'open').replace(/_/g, ' ')}`;
        }
      }
      const lastNoTrade = (latestDay.no_trade_reasons || []).slice(-1)[0] || null;
      return { suffix, latestDay, trade: fTrade, divergence, lastNoTrade };
    });
  }, [followers, days]);

  const minDate = availableDates.length > 0 ? availableDates[availableDates.length - 1] : undefined;
  const maxDate = availableDates.length > 0 ? availableDates[0] : undefined;

  if (loading && days.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
  }

  if (availableDates.length === 0) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        No OPT-SPY-DAILY files found yet. The strategy service writes one file per trading day
        (~16:05 ET) — data will appear here after the first EOD.
      </Alert>
    );
  }

  const latest = agg?.latest;
  const latestTrade = latest && latest.trades_today.length > 0 ? latest.trades_today[0] : null;

  return (
    <Box>
      {/* Header + date range */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
              SPY Daily Credit Spread (GEX)
              <Chip label="0DTE · MAX 1 TRADE/DAY" size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Gamma-driven daily credit spreads on SPY — everything settles same-day, no overnight risk.
              No-trade days are the strategy standing aside by design.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              label="Start"
              type="date"
              size="small"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: minDate, max: endDate || maxDate }}
            />
            <TextField
              label="End"
              type="date"
              size="small"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: startDate || minDate, max: maxDate }}
            />
            {loading && <CircularProgress size={20} />}
          </Box>
        </Box>
      </Paper>

      {agg && latest && (
        <>
          {/* 1. Header KPIs */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} sm={4} md={2.4}>
              <Card>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="caption" color="text.secondary">Realized P&L (period)</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: agg.periodRealized >= 0 ? 'success.main' : 'error.main' }}>
                    {fmtSignedCurrency(agg.periodRealized)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{agg.periodTrades} trade{agg.periodTrades === 1 ? '' : 's'} · {days.length} day{days.length === 1 ? '' : 's'}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <Card>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="caption" color="text.secondary">Realized P&L ({latest.date_et})</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: latest.summary.realized_pnl_today >= 0 ? 'success.main' : 'error.main' }}>
                    {fmtSignedCurrency(latest.summary.realized_pnl_today)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {latest.summary.trades_today === 0 ? 'no trade — stood aside' : `${latest.summary.trades_today} trade`}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <Card>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="caption" color="text.secondary">Win rate (all-time)</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {latest.summary.win_rate != null ? `${(latest.summary.win_rate * 100).toFixed(0)}%` : '—'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {latest.summary.wins_all_time}W / {latest.summary.losses_all_time}L ({latest.summary.closed_all_time} closed)
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <Card>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="caption" color="text.secondary">Current win streak</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: agg.winStreak > 0 ? 'success.main' : 'text.primary' }}>
                    {agg.winStreak}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">consecutive winning days</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <Card>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="caption" color="text.secondary">Premium collected (period)</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {fmtCurrency(agg.periodPremium)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    all-time P&L: {fmtSignedCurrency(latest.summary.realized_pnl_all_time)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* 1b. Accounts strip (v0.9.0 leader/follower fan-out roll-up) */}
          {latest.accounts_summary && latest.accounts_summary.length > 0 && (
            <Paper sx={{ p: 1.5, mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                ACCOUNTS ({latest.date_et})
              </Typography>
              <Table size="small">
                <TableBody>
                  {latest.accounts_summary.map((a) => (
                    <TableRow key={a.account}>
                      <TableCell sx={{ border: 0, py: 0.25, fontWeight: 'bold' }}>
                        {a.account}{' '}
                        <Chip
                          label={a.is_leader ? 'LEADER' : 'FOLLOWER'}
                          size="small"
                          color={a.is_leader ? 'primary' : 'default'}
                          variant="outlined"
                          sx={{ fontSize: '0.6rem', height: 18, ml: 0.5 }}
                        />
                        {LIVE_ACCOUNT_IDS.has(a.account) && (
                          <Chip
                            label="LIVE"
                            size="small"
                            color="error"
                            sx={{ fontSize: '0.6rem', height: 18, ml: 0.5, fontWeight: 'bold' }}
                          />
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ border: 0, py: 0.25 }}>
                        <Typography variant="body2" component="span" sx={{ color: a.realized_pnl_today >= 0 ? 'success.main' : 'error.main', fontWeight: 'bold' }}>
                          {fmtSignedCurrency(a.realized_pnl_today)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" component="span"> today</Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ border: 0, py: 0.25 }}>
                        <Typography variant="body2" component="span" sx={{ color: a.realized_pnl_all_time >= 0 ? 'success.main' : 'error.main' }}>
                          {fmtSignedCurrency(a.realized_pnl_all_time)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" component="span"> all-time</Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ border: 0, py: 0.25 }}>
                        <Typography variant="caption" color="text.secondary">
                          {a.wins_all_time}W / {a.closed_all_time - a.wins_all_time}L ({a.closed_all_time} closed)
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}

          {/* 2. Daily P&L bars + cumulative line */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Daily Realized P&L</Typography>
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart data={agg.chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="left" tick={{ fontSize: 11 }} tickFormatter={(v: number) => `$${v}`} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} tickFormatter={(v: number) => `$${v}`} />
                <Tooltip
                  formatter={(value: any, name: string) => [fmtSignedCurrency(Number(value)), name]}
                  contentStyle={{ backgroundColor: '#1e1e1e', border: '1px solid rgba(255,255,255,0.2)' }}
                />
                <Legend />
                <Bar yAxisId="left" dataKey="pnl" name="Daily P&L" barSize={18}>
                  {agg.chartData.map((entry, index) => (
                    <Cell key={index} fill={entry.pnl >= 0 ? '#4caf50' : '#f44336'} fillOpacity={entry.noTrade ? 0.25 : 1} />
                  ))}
                </Bar>
                <Line yAxisId="right" type="monotone" dataKey="cumulative" name="Cumulative" stroke="#90caf9" strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </Paper>

          {/* 3. Today panel: gamma map chart + today's trade / no-trade reason */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={7}>
              <GexMapView instanceName={instanceName} date={latest.date_et} atmIv={latest.gex?.atm_iv} />
            </Grid>
            <Grid item xs={12} md={5}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  {latestTrade ? `Trade — ${latest.date_et}` : `No Trade — ${latest.date_et}`}
                  {ivBadge(latest.gex?.atm_iv)}
                  {gapBadge(latest.gex?.gap_pct, latest.gex?.gap_vs_sigma)}
                  {eventBadge(latest.event_today ?? latest.gex?.event_today)}
                </Typography>
                {latestTrade ? (
                  <Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
                      <Chip label={`${latestTrade.underlying} ${strikesLabel(latestTrade)}`} color="primary" size="small" sx={{ fontWeight: 'bold' }} />
                      <Chip label={latestTrade.direction === 'call_credit' ? 'CALL CREDIT' : 'PUT CREDIT'} size="small" variant="outlined" />
                      {exitReasonChip(latestTrade.exit_reason, latestTrade.realized_pnl)}
                    </Box>
                    {/* v0.17.2: shadow staircase signal (research-only) on call-spread days */}
                    {latestTrade.direction === 'call_credit' &&
                      (latestTrade.gex_at_entry?.shadow_call_fade_veto ?? latest.gex?.shadow_call_fade_veto) === true && (
                        <Typography variant="caption" sx={{ color: '#ffb74d', display: 'block', mb: 1 }}>
                          The shadow staircase signal would have vetoed this trade (positive-GEX strikes stacked above spot) — a research signal under evaluation, not yet a live gate.
                        </Typography>
                      )}
                    {/* v0.18.0: staircase rule routed a mixed morning to the put side */}
                    {latestTrade.direction === 'put_credit' &&
                      (latest.gex?.stair_up ?? 0) > 0 &&
                      latest.gex?.magnet != null && latest.gex?.call_wall != null &&
                      latest.gex.magnet >= latest.gex.call_wall && (
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
                          The staircase rule routed us to the put side — the tape was being pulled toward the call wall.
                        </Typography>
                      )}
                    <Table size="small">
                      <TableBody>
                        {[
                          ['Contracts', `${contractsLabel(latestTrade)}${isPartialFill(latestTrade) ? ' (partial entry)' : ''}${wallStrikeNote(latestTrade).replace(' · ', ' — ')}`],
                          ['Credit collected', `${fmtCurrency(latestTrade.entry_credit_total)} (${latestTrade.entry_credit_per_share.toFixed(2)}/sh)`],
                          ['Close debit', latestTrade.close_debit_per_share != null ? `${latestTrade.close_debit_per_share.toFixed(2)}/sh` : '—'],
                          ['Max loss (risked)', fmtCurrency(latestTrade.max_loss_total)],
                          ['Opened → Closed', `${fmtTimeEt(latestTrade.opened_at)} → ${fmtTimeEt(latestTrade.closed_at)} ET (${holdTime(latestTrade.opened_at, latestTrade.closed_at)})`],
                          ['% of credit captured', latestTrade.pct_of_credit_captured != null ? `${(latestTrade.pct_of_credit_captured * 100).toFixed(1)}%` : '—'],
                        ].map(([label, value]) => (
                          <TableRow key={label}>
                            <TableCell sx={{ color: 'text.secondary', border: 0, py: 0.5 }}>{label}</TableCell>
                            <TableCell align="right" sx={{ border: 0, py: 0.5 }}>{value}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell sx={{ color: 'text.secondary', border: 0, py: 0.5 }}>Realized P&L</TableCell>
                          <TableCell align="right" sx={{ border: 0, py: 0.5, fontWeight: 'bold', color: latestTrade.realized_pnl >= 0 ? 'success.main' : 'error.main' }}>
                            {fmtSignedCurrency(latestTrade.realized_pnl)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Box>
                ) : (
                  <Box>
                    {(latest.no_trade_reasons || []).length > 0 ? (
                      // Collapse repeated per-scan rows (EVENT_BLACKOUT writes one
                      // per scan, IV gates one per retry) into one line per reason
                      dedupeNoTradeReasons(latest.no_trade_reasons || []).map(({ row, count }, i) => (
                        <Box key={i} sx={{ mb: 1 }}>
                          <Typography variant="body1">{prettyNoTradeReason(row)}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.action} · {fmtTimeEt(row.ts_et)} ET{count > 1 ? ` · ×${count} scans` : ''}
                          </Typography>
                        </Box>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No trade taken this day.
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Standing aside in a negative or mixed gamma regime is the strategy working as intended.
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>

          {/* 3b. Follower account cards (v0.9.0) — same decision, smaller size; no gamma chart here */}
          {followerCards.length > 0 && (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {followerCards.map(({ suffix, latestDay, trade, divergence, lastNoTrade }) => (
                <Grid item xs={12} sm={6} md={4} key={suffix}>
                  <Card variant="outlined" sx={divergence ? { borderColor: 'warning.main' } : undefined}>
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                          {latestDay.account_id || suffix.toUpperCase()}
                        </Typography>
                        <Chip label="FOLLOWER" size="small" variant="outlined" sx={{ fontSize: '0.6rem', height: 18 }} />
                        {(LIVE_ACCOUNT_IDS.has(latestDay.account_id || '') || LIVE_ACCOUNT_SUFFIXES.has(suffix)) && (
                          <Chip label="LIVE" size="small" color="error" sx={{ fontSize: '0.6rem', height: 18, fontWeight: 'bold' }} />
                        )}
                        {divergence && (
                          <Chip label="DIVERGED" size="small" color="warning" sx={{ fontSize: '0.6rem', height: 18, fontWeight: 'bold' }} />
                        )}
                      </Box>
                      {divergence && (
                        <Typography variant="caption" sx={{ color: 'warning.main', display: 'block', mb: 0.5, fontWeight: 'bold' }}>
                          {divergence}
                        </Typography>
                      )}
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {trade
                          ? tradeOneLiner(trade)
                          : lastNoTrade
                            ? prettyNoTradeReason(lastNoTrade)
                            : 'No trade this day.'}
                      </Typography>
                      <Table size="small">
                        <TableBody>
                          <TableRow>
                            <TableCell sx={{ color: 'text.secondary', border: 0, py: 0.25 }}>P&L today ({latestDay.date_et})</TableCell>
                            <TableCell align="right" sx={{ border: 0, py: 0.25, fontWeight: 'bold', color: latestDay.summary.realized_pnl_today >= 0 ? 'success.main' : 'error.main' }}>
                              {fmtSignedCurrency(latestDay.summary.realized_pnl_today)}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ color: 'text.secondary', border: 0, py: 0.25 }}>All-time P&L / record</TableCell>
                            <TableCell align="right" sx={{ border: 0, py: 0.25 }}>
                              <Typography variant="body2" component="span" sx={{ color: latestDay.summary.realized_pnl_all_time >= 0 ? 'success.main' : 'error.main', fontWeight: 'bold' }}>
                                {fmtSignedCurrency(latestDay.summary.realized_pnl_all_time)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" component="span">
                                {' '}· {latestDay.summary.wins_all_time}W / {latestDay.summary.losses_all_time}L
                              </Typography>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ color: 'text.secondary', border: 0, py: 0.25 }}>Equity</TableCell>
                            <TableCell align="right" sx={{ border: 0, py: 0.25 }}>{fmtCurrency(latestDay.account.equity)}</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                        Mirrors the leader's decision at its own sleeve size — gamma map and reasoning are on the main panel above.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}

          {/* 4. Trade log */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              Trade Log ({agg.allTrades.length})
            </Typography>
            {agg.allTrades.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No trades in the selected period.
              </Typography>
            ) : (
              <TableContainer sx={{ maxHeight: 420 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell><strong>Spread</strong></TableCell>
                      <TableCell align="right"><strong>Contracts</strong></TableCell>
                      <TableCell align="right"><strong>Credit</strong></TableCell>
                      <TableCell align="right"><strong>Debit</strong></TableCell>
                      <TableCell align="right"><strong>Realized P&L</strong></TableCell>
                      <TableCell align="right"><strong>% Captured</strong></TableCell>
                      <TableCell><strong>Exit</strong></TableCell>
                      <TableCell align="right"><strong>Hold</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...agg.allTrades].reverse().map((t, i) => (
                      <TableRow key={i} sx={{ bgcolor: t.realized_pnl >= 0 ? 'rgba(76,175,80,0.10)' : 'rgba(244,67,54,0.10)' }}>
                        <TableCell>{t.date_et}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>{t.underlying} {strikesLabel(t)}</TableCell>
                        <TableCell align="right">{contractsLabel(t)}</TableCell>
                        <TableCell align="right">{fmtCurrency(t.entry_credit_total)}</TableCell>
                        <TableCell align="right">{t.close_debit_per_share != null ? `${t.close_debit_per_share.toFixed(2)}/sh` : '—'}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: t.realized_pnl >= 0 ? '#4caf50' : '#f44336' }}>
                          {fmtSignedCurrency(t.realized_pnl)}
                        </TableCell>
                        <TableCell align="right">
                          {t.pct_of_credit_captured != null ? `${(t.pct_of_credit_captured * 100).toFixed(1)}%` : '—'}
                        </TableCell>
                        <TableCell>{exitReasonChip(t.exit_reason, t.realized_pnl)}</TableCell>
                        <TableCell align="right">{holdTime(t.opened_at, t.closed_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>

          {/* 4b. v0.69.0 LIVE floor AI vs the fixed 30% floor — leader
              FLOOR_AI_CLOSE rows, all dates since the rule went live */}
          {floorAi && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                Floor AI vs fixed floor (LIVE since v0.69.0, 10/30) — {floorAi.totals.n} watched trade{floorAi.totals.n === 1 ? '' : 's'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
                When a grade B/C trade's profit floor fires (35% → 30% captured; no floor on credits ≤ $0.10) the AI decides the exit instead of the fixed floor.
                Score = what the trade actually made minus what the fixed floor would have made (≈, leader, per contract).
              </Typography>
              {floorAi.totals.n === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>No floor fires since the floor AI went live.</Typography>
              ) : (
                <>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Σ vs fixed floor{' '}
                    <b style={{ color: (floorAi.totals.sum_vs_fixed_floor_per_ct ?? 0) >= 0 ? '#4caf50' : '#f44336' }}>
                      {floorAi.totals.sum_vs_fixed_floor_per_ct != null ? `${floorAi.totals.sum_vs_fixed_floor_per_ct >= 0 ? '+' : '−'}$${Math.abs(floorAi.totals.sum_vs_fixed_floor_per_ct).toFixed(0)}/ct` : '—'}
                    </b>
                    {floorAi.totals.worst_vs_fixed_floor_per_ct != null && ` · worst trade ${floorAi.totals.worst_vs_fixed_floor_per_ct >= 0 ? '+' : '−'}$${Math.abs(floorAi.totals.worst_vs_fixed_floor_per_ct).toFixed(0)}/ct`}
                    {floorAi.totals.n_fallback > 0 && ` · ${floorAi.totals.n_fallback} fell back to the fixed floor`}
                  </Typography>
                  <TableContainer sx={{ maxHeight: 300 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          {['Date', 'Grade', 'Watch from', 'Exit', 'P&L/ct', 'Fixed floor ≈', 'vs floor', 'Votes (exit/hold/err)', 'AI reason'].map((h) => (
                            <TableCell key={h}><strong>{h}</strong></TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {floorAi.trades.map((r, i) => {
                          const $ct = (v: any) => (v != null ? `${Number(v) >= 0 ? '+' : '−'}$${Math.abs(Number(v)).toFixed(0)}` : '—');
                          return (
                            <TableRow key={i}>
                              <TableCell>{r.date}</TableCell>
                              <TableCell>{r.entry_grade ?? '—'}</TableCell>
                              <TableCell>{r.watch_since ? String(r.watch_since).slice(11, 16) : '—'}</TableCell>
                              <TableCell>
                                {exitReasonChip(r.exit_reason ?? null, Number(r.pnl_per_ct ?? 0))}
                                {r.fallback && <Chip label="fallback" size="small" variant="outlined" color="warning" sx={{ ml: 0.5, fontSize: '0.6rem', height: 18 }} />}
                              </TableCell>
                              <TableCell sx={{ fontWeight: 'bold', color: Number(r.pnl_per_ct ?? 0) >= 0 ? '#4caf50' : '#f44336' }}>{$ct(r.pnl_per_ct)}</TableCell>
                              <TableCell>{$ct(r.fixed_floor_pnl_per_ct_approx)}</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', color: Number(r.vs_fixed_floor_per_ct ?? 0) >= 0 ? '#4caf50' : '#f44336' }}>{$ct(r.vs_fixed_floor_per_ct)}</TableCell>
                              <TableCell>{r.n_exit ?? 0}/{r.n_hold ?? 0}/{(r.n_errors ?? 0) + (r.n_misreads ?? 0)}</TableCell>
                              <TableCell sx={{ maxWidth: 320, fontSize: '0.75rem' }}>{r.ai_exit_reason ?? '—'}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Paper>
          )}

          {/* 5. Exit-reason breakdown */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Exit-Reason Breakdown</Typography>
            {agg.exitBreakdown.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No closed trades in the selected period.
              </Typography>
            ) : (
              <Grid container spacing={2}>
                {agg.exitBreakdown.map(([reason, stats]) => (
                  <Grid item xs={6} sm={4} md={2.4} key={reason}>
                    <Card variant="outlined">
                      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        {exitReasonChip(reason, stats.pnl)}
                        <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 0.5 }}>
                          {stats.count}
                        </Typography>
                        <Typography variant="caption" sx={{ color: stats.pnl >= 0 ? 'success.main' : 'error.main', fontWeight: 'bold' }}>
                          {fmtSignedCurrency(stats.pnl)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Paper>
        </>
      )}
    </Box>
  );
};

export default OptSpyDailyView;
